/**
 * Centralized Token Refresh Service
 * 
 * This service coordinates token refresh across HTTP requests and WebSocket connections
 * to prevent race conditions and ensure both use the same refreshed token.
 */

type TokenRefreshCallback = (token: string) => void;

class TokenRefreshService {
  private isRefreshing: boolean = false;
  private refreshSubscribers: TokenRefreshCallback[] = [];
  private lastRefreshTime: number = 0;
  private readonly REFRESH_COOLDOWN = 30000; // 30 seconds cooldown between refresh attempts
  private refreshPromise: Promise<string> | null = null;

  /**
   * Refresh the token and notify all subscribers
   */
  private async performRefresh(): Promise<string> {
    try {
      console.log("🔄 [TokenRefreshService] Starting token refresh...");
      
      const baseUrl = process.env.REACT_APP_BASE_URL || '';
      const refreshUrl = `${baseUrl}/payments/api/token/refresh/`;
      
      // Use fetch with credentials to match cookie-based auth
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      let response: Response;
      try {
        response = await fetch(refreshUrl, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);

        if (!response.ok) {
          let errorMessage = `Token refresh failed: ${response.status}`;
          try {
            const errorData = await response.text();
            if (errorData) {
              errorMessage += ` - ${errorData}`;
            }
          } catch (parseError) {
            // If we can't parse the error response, just use the status
          }
          throw new Error(errorMessage);
        }

        let data: any;
        try {
          data = await response.json();
        } catch (jsonError) {
          throw new Error("Invalid JSON response from token refresh endpoint");
        }
        
        const newAccessToken = data?.access;

        if (!newAccessToken) {
          throw new Error("No token in refresh response");
        }

        // Update localStorage - this is the single source of truth
        try {
          localStorage.setItem('access_token', newAccessToken);
        } catch (storageError) {
          console.error("❌ [TokenRefreshService] Failed to save token to localStorage:", storageError);
          // Continue anyway - token is still valid for this request
        }
        this.lastRefreshTime = Date.now();
        
        console.log("✅ [TokenRefreshService] Token refreshed successfully");

        // Notify all subscribers
        this.notifySubscribers(newAccessToken);

        return newAccessToken;
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          throw new Error('Token refresh request timed out');
        }
        throw fetchError;
      }
    } catch (error) {
      console.error("❌ [TokenRefreshService] Token refresh failed:", error);
      // Clear subscribers on error
      this.refreshSubscribers = [];
      throw error;
    }
  }

  /**
   * Notify all subscribers that token has been refreshed
   */
  private notifySubscribers(token: string): void {
    this.refreshSubscribers.forEach((callback) => {
      try {
        callback(token);
      } catch (error) {
        console.error("❌ [TokenRefreshService] Error in subscriber callback:", error);
      }
    });
    this.refreshSubscribers = [];
  }

  /**
   * Get a refreshed token, coordinating with other refresh attempts
   * 
   * @param forceRefresh - Force a refresh even if one is in progress
   * @returns Promise that resolves with the new token
   */
  async refreshToken(forceRefresh: boolean = false): Promise<string> {
    // If already refreshing, wait for the existing refresh to complete
    // This check should happen FIRST to coordinate concurrent requests
    if (this.isRefreshing && this.refreshPromise) {
      console.log("⏳ [TokenRefreshService] Refresh already in progress, waiting...");
      return this.refreshPromise;
    }

    // Check cooldown period (only if not forcing and not already refreshing)
    const now = Date.now();
    if (!forceRefresh && (now - this.lastRefreshTime) < this.REFRESH_COOLDOWN) {
      const timeSinceRefresh = now - this.lastRefreshTime;
      console.log(`⏳ [TokenRefreshService] Refresh in cooldown (${Math.round((this.REFRESH_COOLDOWN - timeSinceRefresh) / 1000)}s remaining)`);
      
      // Return existing token from localStorage, but validate it's not expired
      const existingToken = this.getCurrentToken();
      if (existingToken) {
        // Check if token is still valid (not expired) - parse the token we already have
        try {
          const base64Url = existingToken.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split('')
              .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join('')
          );
          const tokenData = JSON.parse(jsonPayload);
          const tokenExpiry = tokenData.exp - Math.floor(Date.now() / 1000);
          
          // If token is still valid (not expired), return it
          if (tokenExpiry > 0) {
            return existingToken;
          }
          // Token exists but is expired - need to refresh despite cooldown
          console.log("⚠️ [TokenRefreshService] Token in cooldown but expired, forcing refresh");
        } catch (parseError) {
          // If we can't parse the token, assume it's invalid and refresh
          console.log("⚠️ [TokenRefreshService] Token in cooldown but invalid format, forcing refresh");
        }
      } else {
        // No token available - must refresh
        console.log("⚠️ [TokenRefreshService] No token available, forcing refresh despite cooldown");
      }
    }

    // Start new refresh
    this.isRefreshing = true;
    this.refreshPromise = this.performRefresh()
      .then((token) => {
        this.isRefreshing = false;
        this.refreshPromise = null;
        return token;
      })
      .catch((error) => {
        this.isRefreshing = false;
        this.refreshPromise = null;
        throw error;
      });

    return this.refreshPromise;
  }

  /**
   * Subscribe to token refresh events
   * Callback will be called when token is refreshed
   * 
   * @param callback - Function to call when token is refreshed
   */
  subscribe(callback: TokenRefreshCallback): void {
    this.refreshSubscribers.push(callback);
  }

  /**
   * Check if a refresh is currently in progress
   */
  isRefreshInProgress(): boolean {
    return this.isRefreshing;
  }

  /**
   * Wait for current refresh to complete (if any)
   */
  async waitForRefresh(): Promise<string | null> {
    if (this.isRefreshing && this.refreshPromise) {
      try {
        return await this.refreshPromise;
      } catch (error) {
        return null;
      }
    }
    try {
      return localStorage.getItem('access_token');
    } catch (error) {
      console.error("❌ [TokenRefreshService] Failed to read from localStorage:", error);
      return null;
    }
  }

  /**
   * Get current token from localStorage
   */
  getCurrentToken(): string | null {
    try {
      return localStorage.getItem('access_token');
    } catch (error) {
      console.error("❌ [TokenRefreshService] Failed to read from localStorage:", error);
      return null;
    }
  }

  /**
   * Check if token is expired or about to expire
   * 
   * @param bufferSeconds - Refresh if token expires within this many seconds (default: 300 = 5 minutes)
   * @returns true if token should be refreshed
   */
  shouldRefreshToken(bufferSeconds: number = 300): boolean {
    const token = this.getCurrentToken();
    if (!token) {
      // No token in localStorage means the user hasn't logged in yet or
      // localStorage hasn't been updated yet (timing gap after login).
      // Returning true here would cause the WebSocket to fire a refresh
      // against a cookie that may belong to a brand-new session —
      // unnecessarily rotating it. Callers that need a token should
      // redirect to login instead of refreshing.
      return false;
    }

    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const tokenData = JSON.parse(jsonPayload);

      const now = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = tokenData.exp - now;

      return timeUntilExpiry < bufferSeconds;
    } catch (error) {
      console.error("❌ [TokenRefreshService] Error parsing token:", error);
      // Malformed token — don't refresh speculatively, let the next API
      // call fail normally and be handled by the HTTP interceptor.
      return false;
    }
  }
}

// Export singleton instance
export const tokenRefreshService = new TokenRefreshService();

